import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { billingsAPI, tenantsAPI, unitsAPI, propertiesAPI } from '../lib/api';
import { formatDate, formatCurrency, COST_TYPE_LABELS, DISTRIBUTION_KEY_LABELS } from '../lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  FileText,
  Download,
  Trash2,
  Loader2,
  Search,
  Building2,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Eye,
  Send
} from 'lucide-react';

export default function Billings() {
  const [billings, setBillings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPropertyId, setFilterPropertyId] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewBilling, setViewBilling] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [formData, setFormData] = useState({
    property_id: '',
    tenant_id: '',
    period_start: '',
    period_end: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [billingsRes, propsRes, unitsRes, tenantsRes] = await Promise.all([
        billingsAPI.getAll(),
        propertiesAPI.getAll(),
        unitsAPI.getAll(),
        tenantsAPI.getAll()
      ]);
      setBillings(billingsRes.data);
      setProperties(propsRes.data);
      setUnits(unitsRes.data);
      setTenants(tenantsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    const currentYear = new Date().getFullYear() - 1;
    setFormData({
      property_id: properties.length > 0 ? properties[0].id : '',
      tenant_id: '',
      period_start: `${currentYear}-01-01`,
      period_end: `${currentYear}-12-31`
    });
  };

  const getTenantsForProperty = (propertyId) => {
    const propertyUnits = units.filter(u => u.property_id === propertyId);
    const unitIds = propertyUnits.map(u => u.id);
    return tenants.filter(t => unitIds.includes(t.unit_id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await billingsAPI.create(formData);
      toast.success('Abrechnung erstellt');
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async (billingId) => {
    try {
      await billingsAPI.finalize(billingId);
      toast.success('Abrechnung finalisiert');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Finalisieren');
    }
  };

  const handleDownload = async (billingId) => {
    setDownloadingId(billingId);
    try {
      const response = await billingsAPI.downloadPdf(billingId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Nebenkostenabrechnung_${billingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Download');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await billingsAPI.delete(deleteId);
      toast.success('Abrechnung gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteId(null);
    }
  };

  const handleSendEmail = async (billing) => {
    if (!window.confirm(`Abrechnung per E-Mail an ${getTenantName(billing.tenant_id)} senden?`)) return;
    setSendingId(billing.id);
    try {
      const res = await billingsAPI.sendEmail(billing.id);
      toast.success(`E-Mail an ${res.data.recipient} gesendet`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'E-Mail-Versand fehlgeschlagen');
    } finally {
      setSendingId(null);
    }
  };

  const getTenantName = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.name : 'Unbekannt';
  };

  const getPropertyName = (propertyId) => {
    const prop = properties.find(p => p.id === propertyId);
    return prop ? prop.name : 'Unbekannt';
  };

  const getUnitName = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit ? unit.name : 'Unbekannt';
  };

  const filteredBillings = billings.filter(b => {
    const matchesProperty = filterPropertyId === 'all' || b.property_id === filterPropertyId;
    const matchesStatus = filterStatus === 'all' || b.status === filterStatus;
    return matchesProperty && matchesStatus;
  });

  const availableTenants = formData.property_id 
    ? getTenantsForProperty(formData.property_id)
    : [];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="billings-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Abrechnungen
            </h1>
            <p className="text-slate-500 mt-1">Erstellen und verwalten Sie Nebenkostenabrechnungen</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-billing-button" 
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={tenants.length === 0}
              >
                <Plus className="mr-2 h-4 w-4" />
                Abrechnung erstellen
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Neue Abrechnung</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="property_id">
                    Immobilie <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.property_id}
                    onValueChange={(value) => setFormData({ 
                      ...formData, 
                      property_id: value,
                      tenant_id: ''
                    })}
                    required
                  >
                    <SelectTrigger data-testid="billing-property-select">
                      <SelectValue placeholder="Immobilie auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map((prop) => (
                        <SelectItem key={prop.id} value={prop.id}>
                          {prop.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tenant_id">
                    Mieter <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.tenant_id}
                    onValueChange={(value) => setFormData({ ...formData, tenant_id: value })}
                    disabled={!formData.property_id}
                    required
                  >
                    <SelectTrigger data-testid="billing-tenant-select">
                      <SelectValue placeholder="Mieter auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTenants.map((tenant) => {
                        const unit = units.find(u => u.id === tenant.unit_id);
                        return (
                          <SelectItem key={tenant.id} value={tenant.id}>
                            {tenant.name} ({unit?.name})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="period_start">
                      Zeitraum von <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="period_start"
                      type="date"
                      data-testid="billing-period-start"
                      value={formData.period_start}
                      onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="period_end">
                      Zeitraum bis <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="period_end"
                      type="date"
                      data-testid="billing-period-end"
                      value={formData.period_end}
                      onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Abbrechen
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="billing-save-button">
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Erstellen...
                      </>
                    ) : (
                      'Abrechnung erstellen'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Select value={filterPropertyId} onValueChange={setFilterPropertyId}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="billing-filter-property">
              <SelectValue placeholder="Alle Immobilien" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Immobilien</SelectItem>
              {properties.map((prop) => (
                <SelectItem key={prop.id} value={prop.id}>
                  {prop.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="billing-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="draft">Entwürfe</SelectItem>
              <SelectItem value="final">Finalisiert</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* No Tenants Warning */}
        {tenants.length === 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <p className="text-amber-800 text-sm">
                <Building2 className="inline h-4 w-4 mr-2" />
                Erstellen Sie zuerst Mieter, um Abrechnungen erstellen zu können.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Billings List */}
        {filteredBillings.length > 0 ? (
          <Card className="border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold">Mieter</TableHead>
                  <TableHead className="font-semibold">Immobilie / Einheit</TableHead>
                  <TableHead className="font-semibold">Zeitraum</TableHead>
                  <TableHead className="font-semibold text-right">Ergebnis</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="w-[150px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBillings.map((billing) => (
                  <TableRow key={billing.id} data-testid={`billing-row-${billing.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {getTenantName(billing.tenant_id)}
                          </p>
                          <p className="text-sm text-slate-500">
                            {formatCurrency(billing.tenant_share)} Anteil
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-slate-700">{getPropertyName(billing.property_id)}</p>
                        <p className="text-sm text-slate-500">{getUnitName(billing.unit_id)}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {formatDate(billing.period_start)} - {formatDate(billing.period_end)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className={`flex items-center justify-end gap-1 font-semibold tabular-nums ${
                        billing.balance > 0 ? 'text-red-600' : 'text-emerald-600'
                      }`}>
                        {billing.balance > 0 ? (
                          <TrendingUp className="h-4 w-4" />
                        ) : (
                          <TrendingDown className="h-4 w-4" />
                        )}
                        {formatCurrency(Math.abs(billing.balance))}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {billing.balance > 0 ? 'Nachzahlung' : 'Guthaben'}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={billing.status === 'final' ? 'default' : 'secondary'}
                        className={billing.status === 'final' 
                          ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' 
                          : 'bg-amber-100 text-amber-800'
                        }
                      >
                        {billing.status === 'final' ? 'Final' : 'Entwurf'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`view-billing-${billing.id}`}
                          onClick={() => setViewBilling(billing)}
                        >
                          <Eye className="h-4 w-4 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`download-billing-${billing.id}`}
                          onClick={() => handleDownload(billing.id)}
                          disabled={downloadingId === billing.id}
                        >
                          {downloadingId === billing.id ? (
                            <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                          ) : (
                            <Download className="h-4 w-4 text-slate-500" />
                          )}
                        </Button>
                        {billing.status === 'draft' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`finalize-billing-${billing.id}`}
                            onClick={() => handleFinalize(billing.id)}
                          >
                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                          </Button>
                        )}
                        {billing.status === 'final' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`send-billing-${billing.id}`}
                            onClick={() => handleSendEmail(billing)}
                            disabled={sendingId === billing.id}
                            title={billing.sent_at ? `Zuletzt versendet: ${formatDate(billing.sent_at)}` : 'An Mieter senden'}
                          >
                            {sendingId === billing.id ? (
                              <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                            ) : (
                              <Send className={`h-4 w-4 ${billing.sent_at ? 'text-emerald-600' : 'text-blue-600'}`} />
                            )}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`delete-billing-${billing.id}`}
                          onClick={() => setDeleteId(billing.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {filterPropertyId !== 'all' || filterStatus !== 'all' 
                  ? 'Keine Abrechnungen gefunden' 
                  : 'Noch keine Abrechnungen'
                }
              </h3>
              <p className="text-slate-500 mb-4">
                {filterPropertyId !== 'all' || filterStatus !== 'all'
                  ? 'Versuchen Sie andere Filtereinstellungen' 
                  : 'Erstellen Sie Ihre erste Nebenkostenabrechnung'
                }
              </p>
              {filterPropertyId === 'all' && filterStatus === 'all' && tenants.length > 0 && (
                <Button onClick={() => setIsDialogOpen(true)} data-testid="empty-create-billing-button">
                  <Plus className="mr-2 h-4 w-4" />
                  Erste Abrechnung erstellen
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* View Billing Dialog */}
        <Dialog open={!!viewBilling} onOpenChange={() => setViewBilling(null)}>
          <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Abrechnungsdetails</DialogTitle>
            </DialogHeader>
            {viewBilling && (
              <div className="space-y-6 mt-4">
                {/* Summary */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-500">Mieter</p>
                    <p className="font-medium">{getTenantName(viewBilling.tenant_id)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Zeitraum</p>
                    <p className="font-medium">
                      {formatDate(viewBilling.period_start)} - {formatDate(viewBilling.period_end)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Immobilie</p>
                    <p className="font-medium">{getPropertyName(viewBilling.property_id)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Einheit</p>
                    <p className="font-medium">{getUnitName(viewBilling.unit_id)}</p>
                  </div>
                </div>

                {/* Cost Details */}
                <div>
                  <h4 className="font-medium mb-3">Kostenaufstellung</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Kostenart</TableHead>
                          <TableHead className="text-xs text-right">Gesamt</TableHead>
                          <TableHead className="text-xs text-right">Anteil</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {viewBilling.cost_details.map((detail, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="py-2">
                              <div>
                                <p className="text-sm font-medium">
                                  {COST_TYPE_LABELS[detail.cost_type] || detail.cost_type}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {DISTRIBUTION_KEY_LABELS[detail.distribution_key]} ({(detail.share_ratio * 100).toFixed(1)}%)
                                </p>
                              </div>
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-sm">
                              {formatCurrency(detail.total_amount)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-sm font-medium">
                              {formatCurrency(detail.share_amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Final Calculation */}
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Gesamtkosten Objekt</span>
                    <span className="tabular-nums">{formatCurrency(viewBilling.total_costs)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Ihr Kostenanteil</span>
                    <span className="tabular-nums">{formatCurrency(viewBilling.tenant_share)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Geleistete Vorauszahlungen</span>
                    <span className="tabular-nums">- {formatCurrency(viewBilling.prepayments)}</span>
                  </div>
                  <div className="flex justify-between font-semibold pt-2 border-t">
                    <span>{viewBilling.balance > 0 ? 'Nachzahlung' : 'Guthaben'}</span>
                    <span className={`tabular-nums ${viewBilling.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {formatCurrency(Math.abs(viewBilling.balance))}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => handleDownload(viewBilling.id)}
                    disabled={downloadingId === viewBilling.id}
                  >
                    {downloadingId === viewBilling.id ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    PDF herunterladen
                  </Button>
                  {viewBilling.status === 'draft' && (
                    <Button onClick={() => {
                      handleFinalize(viewBilling.id);
                      setViewBilling(null);
                    }}>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Finalisieren
                    </Button>
                  )}
                  {viewBilling.status === 'final' && (
                    <Button
                      data-testid="dialog-send-billing"
                      onClick={() => handleSendEmail(viewBilling)}
                      disabled={sendingId === viewBilling.id}
                      className="bg-slate-900 hover:bg-slate-800"
                    >
                      {sendingId === viewBilling.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="mr-2 h-4 w-4" />
                      )}
                      {viewBilling.sent_at ? 'Erneut an Mieter senden' : 'An Mieter senden'}
                    </Button>
                  )}
                </div>
                {viewBilling.sent_at && (
                  <p className="text-xs text-emerald-600 text-right">
                    Zuletzt gesendet am {formatDate(viewBilling.sent_at)} an {viewBilling.sent_to}
                  </p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Abrechnung löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Diese Aktion kann nicht rückgängig gemacht werden. Die Abrechnung 
                wird dauerhaft entfernt.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-billing"
              >
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
